import { ApiError } from './http';
import { ApiErrorCodes } from './errors';
import type { AiApi } from './contract';
import type {
  AiAssistRequest,
  AiAssistResult,
  AiLatestChoice,
  AiListedModel,
  AiModel,
  AiModels,
  AiSelectedModel,
  AiSettings,
} from './types';

/**
 * MOCK: the AI assistant (ADR 0065), for `VITE_USE_MOCKS=true` and tests.
 *
 * It follows the real server's rules rather than being kinder than it (the
 * HANDOFF lesson): off until a key is saved AND the switch is turned on; the
 * key is write-only (nothing here ever returns it); a field the action does
 * not use is refused; the answer arrives in pieces. The "model" is a few
 * string rules, enough to see every control do something real.
 *
 * Keys the mock understands, so the settings page can be driven end to end:
 *   - anything starting `sk-ant-` and at least 20 characters is accepted;
 *   - one containing `invalid` is refused as Anthropic refuses a bad key;
 *   - one containing `nocredit` is accepted but its "account" has no credit;
 *   - one containing `fable` can also use the Fable family;
 *   - one containing `modelsdown` gets the built-in list, as when Anthropic's
 *     model list cannot be reached.
 */

export const MOCK_AI_MODELS: AiModel[] = [
  { id: 'claude-sonnet-5', label: 'Claude Sonnet 5', note: 'Good writing, quick.' },
  { id: 'claude-opus-5-5', label: 'Claude Opus 5.5', note: 'The most careful. Slower, and costs more per use.' },
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', note: 'The fastest and cheapest.' },
];

/** The server's default choice: follow Sonnet releases. */
export const MOCK_AI_DEFAULT_MODEL = 'latest:sonnet';

/** The families "always use the latest" is offered for, in the server's order. */
const LATEST_FAMILIES = ['sonnet', 'opus', 'haiku', 'fable'] as const;

/**
 * MOCK: what Anthropic's model list says for a good key, newest first. The
 * "new" model is Sonnet 5.5; the Mythos preview is newer than Opus 5.5 but a
 * variant, which "latest" must never pick.
 */
export const MOCK_AI_LIVE_MODELS: AiListedModel[] = [
  {
    id: 'claude-sonnet-5-5',
    displayName: 'Claude Sonnet 5.5',
    createdAt: '2026-09-28T00:00:00Z',
    family: 'sonnet',
    variant: false,
    maxInputTokens: 1000000,
    maxTokens: 128000,
    effortLevels: ['low', 'medium', 'high'],
    adaptiveThinking: true,
  },
  {
    id: 'claude-opus-5-5-mythos-preview',
    displayName: 'Claude Opus 5.5 Mythos Preview',
    createdAt: '2026-09-15T00:00:00Z',
    family: 'opus',
    variant: true,
    maxInputTokens: 1000000,
    maxTokens: 128000,
    effortLevels: ['low', 'medium', 'high', 'max'],
    adaptiveThinking: true,
  },
  {
    id: 'claude-opus-5-5',
    displayName: 'Claude Opus 5.5',
    createdAt: '2026-08-12T00:00:00Z',
    family: 'opus',
    variant: false,
    maxInputTokens: 1000000,
    maxTokens: 128000,
    effortLevels: ['low', 'medium', 'high', 'max'],
    adaptiveThinking: true,
  },
  {
    id: 'claude-sonnet-5',
    displayName: 'Claude Sonnet 5',
    createdAt: '2026-05-20T00:00:00Z',
    family: 'sonnet',
    variant: false,
    maxInputTokens: 1000000,
    maxTokens: 64000,
    effortLevels: ['low', 'medium', 'high'],
    adaptiveThinking: true,
  },
  {
    id: 'claude-haiku-4-5-20251001',
    displayName: 'Claude Haiku 4.5',
    createdAt: '2025-10-01T00:00:00Z',
    family: 'haiku',
    variant: false,
    maxInputTokens: 200000,
    maxTokens: 64000,
    effortLevels: [],
    adaptiveThinking: false,
  },
];

/** MOCK: the Fable model, listed only for a key containing `fable`. */
export const MOCK_AI_FABLE_MODEL: AiListedModel = {
  id: 'claude-fable-5-1',
  displayName: 'Claude Fable 5.1',
  createdAt: '2026-09-20T00:00:00Z',
  family: 'fable',
  variant: false,
  maxInputTokens: 200000,
  maxTokens: 64000,
  effortLevels: ['low', 'medium', 'high'],
  adaptiveThinking: true,
};

/** The server's refresh limit: `?refresh=1` this many times a minute per person. */
export const MOCK_AI_MODELS_REFRESH_PER_MINUTE = 3;

const FAMILY_NAMES: Record<string, string> = { sonnet: 'Sonnet', opus: 'Opus', haiku: 'Haiku', fable: 'Fable' };

function familyOf(id: string): string {
  return /^claude-(opus|sonnet|haiku|fable)\b/.exec(id)?.[1] ?? 'other';
}

/** The built-in list as `GET /ai/models` answers it when Anthropic cannot be reached. */
function fallbackModels(): AiListedModel[] {
  return MOCK_AI_MODELS.map((m) => ({
    id: m.id,
    displayName: m.label,
    family: familyOf(m.id),
    variant: false,
    effortLevels: [],
    adaptiveThinking: false,
  }));
}

/** The key's catalogue: what Anthropic lists for it, newest first. */
function catalogueFor(key: string): AiListedModel[] {
  const list = MOCK_AI_LIVE_MODELS.map((m) => ({ ...m }));
  if (key.includes('fable')) list.splice(1, 0, { ...MOCK_AI_FABLE_MODEL });
  return list;
}

/** The newest plain (non-variant) model of a family; list order stands in for missing dates. */
function latestOf(family: string, list: AiListedModel[]): AiListedModel | undefined {
  let best: AiListedModel | undefined;
  for (const m of list) {
    if (m.family !== family || m.variant === true) continue;
    if (best === undefined) best = m;
    else if (m.createdAt !== undefined && best.createdAt !== undefined && m.createdAt > best.createdAt) best = m;
  }
  return best;
}

function latestEntries(list: AiListedModel[]): AiLatestChoice[] {
  return LATEST_FAMILIES.flatMap((family) => {
    const m = latestOf(family, list);
    // Fable is offered only when the key has it; the others always are.
    if (family === 'fable' && m === undefined) return [];
    return [{ choice: `latest:${family}`, family, ...(m ? { id: m.id, displayName: m.displayName } : {}) }];
  });
}

/** MOCK: the server's resolution of a saved choice against a key's list. */
export function mockResolveModel(choice: string, list: AiListedModel[]): AiSelectedModel {
  if (choice.startsWith('latest:')) {
    const m = latestOf(choice.slice('latest:'.length), list);
    return { choice, ...(m ? { id: m.id, displayName: m.displayName } : {}) };
  }
  const exact = list.find((m) => m.id === choice);
  if (exact) return { choice, id: exact.id, displayName: exact.displayName };
  const family = familyOf(choice);
  const name = MOCK_AI_MODELS.find((m) => m.id === choice)?.label;
  const said = name !== undefined ? `${name} (${choice})` : choice;
  const replacement = latestOf(family, list);
  const familyName = FAMILY_NAMES[family];
  if (replacement === undefined || familyName === undefined) {
    return { choice, retired: true, notice: `${said} is no longer available to your key. Choose another model.` };
  }
  return {
    choice,
    id: replacement.id,
    displayName: replacement.displayName,
    retired: true,
    notice: `${said} is no longer available to your key, so the latest ${familyName} is used instead.`,
  };
}

const MAX_INPUT_CHARS = 60000;

function aiError(status: number, code: string, message: string, details?: Record<string, unknown>): ApiError {
  return new ApiError(status, {
    error: { code, message, ...(details ? { details } : {}), request_id: 'mock-ai' },
  });
}

function keyProblem(key: string): ApiError | undefined {
  if (key.includes('invalid')) {
    return aiError(422, ApiErrorCodes.AIKeyRejected,
      'Anthropic did not accept your API key. Check it in Settings → AI assistant.',
      { upstream_message: 'invalid x-api-key', settings_path: '/settings/ai' });
  }
  if (key.includes('nocredit')) {
    return aiError(402, ApiErrorCodes.AINoCredit,
      'Your Anthropic account is out of credit. Add credit in the Anthropic Console, then try again.',
      { upstream_message: 'Your credit balance is too low to access the Anthropic API.', settings_path: '/settings/ai' });
  }
  return undefined;
}

function looksLikeKey(key: string): boolean {
  return /^\S{20,512}$/.test(key);
}

/** The server's per-action field allow-list, mirrored. */
function validate(req: AiAssistRequest): string | undefined {
  const has = (s: string | undefined): boolean => (s ?? '').trim() !== '';
  const allowed: Record<string, (keyof AiAssistRequest)[]> = {
    draft: ['instruction', 'subject'],
    rewrite: ['style', 'text'],
    proofread: ['text'],
    reply: ['instruction', 'text', 'subject', 'thread'],
    review: ['text', 'subject', 'thread', 'attachmentNames'],
  };
  const fields = allowed[req.action];
  if (fields === undefined) return 'Unknown assistant action.';
  for (const key of ['style', 'instruction', 'text', 'subject', 'thread', 'attachmentNames'] as const) {
    const value = req[key];
    const present = Array.isArray(value) ? value.length > 0 : value !== undefined && value !== '';
    if (present && !fields.includes(key)) return `This action does not take ${key}.`;
  }
  if (req.action === 'draft' && !has(req.instruction)) return 'Say what the email should be about.';
  if ((req.action === 'rewrite' || req.action === 'proofread' || req.action === 'review') && !has(req.text)) {
    return 'There is no text to work on yet.';
  }
  if (req.action === 'reply' && !has(req.thread)) return 'There is no message to reply to.';
  return undefined;
}

const FIXES: [RegExp, string][] = [
  [/\bteh\b/g, 'the'],
  [/\brecieve\b/g, 'receive'],
  [/\bdefinately\b/g, 'definitely'],
  [/\bseperate\b/g, 'separate'],
  [/\balot\b/g, 'a lot'],
  [/\bi\b/g, 'I'],
  [/ {2,}/g, ' '],
];

function capitalise(text: string): string {
  return text.replace(/(^|[.!?]\s+|\n\s*)([a-z])/g, (_m, lead: string, ch: string) => lead + ch.toUpperCase());
}

/** MOCK: what "Claude" answers. Deterministic, so tests can assert it. */
export function mockAiAnswer(req: AiAssistRequest): string {
  const text = (req.text ?? '').trim();
  switch (req.action) {
    case 'draft':
      return `Hi,\n\nI'm writing about ${(req.instruction ?? '').trim().replace(/[.!]+$/, '')}. Could you let me know what works for you?\n\nThanks,`;
    case 'proofread': {
      let out = text;
      for (const [re, fix] of FIXES) out = out.replace(re, fix);
      return capitalise(out);
    }
    case 'rewrite': {
      switch (req.style) {
        case 'shorter':
          return text
            .split(/\n{2,}/)
            .map((p) => (/^.*?[.!?](\s|$)/.exec(p)?.[0] ?? p).trim())
            .join('\n\n');
        case 'formal':
          return capitalise(
            text
              .replace(/^(hey|hi)\b[ ,]*/i, 'Dear colleague,\n\n')
              .replace(/\bthanks\b/gi, 'Thank you')
              .replace(/\bcan't\b/gi, 'cannot')
              .replace(/\bwon't\b/gi, 'will not'),
          );
        case 'friendlier':
          return `Hi there!\n\n${text}\n\nHope you're having a good week.`;
        default:
          return capitalise(text.replace(/\s+/g, ' ').replace(/ ([,.])/g, '$1'));
      }
    }
    case 'reply':
      return `Hi,\n\nThanks for your message. ${(req.instruction ?? '').trim() || 'That works for me.'}\n\nBest,`;
    case 'review': {
      const tips: string[] = [];
      if (/\battach/i.test(text) && (req.attachmentNames ?? []).length === 0) {
        tips.push('- You mention an attachment, but nothing is attached yet.');
      }
      if ((req.thread ?? '').includes('?')) {
        tips.push('- The message you are replying to asks a question; check this answers it.');
      }
      if (text.length < 40) tips.push('- It is very short; a line of context would help the reader.');
      return tips.length > 0 ? tips.join('\n') : 'Looks good to send.';
    }
  }
  return '';
}

/**
 * A received message as the mock server reads it for Summarize (ADR 0074):
 * plain text, headers, attachment names. The real server renders it from the
 * sanitized MIME; the mock gets it from the mock mailbox.
 */
export interface MockReadableMessage {
  subject: string;
  from: string;
  text: string;
  inJunk: boolean;
  attachments: { name: string; type: string }[];
}

/** The server's `MaxEmailTextChars`. */
export const MOCK_MAX_EMAIL_TEXT_CHARS = 40000;

/**
 * The keys `POST …/messages/{id}/assist` accepts: the Go struct's json tags,
 * camel-cased (the contract test pins the two). Anything else is refused as
 * `DisallowUnknownFields` refuses it.
 */
export const MOCK_MESSAGE_ASSIST_FIELDS = ['action', 'question', 'wholeThread', 'conversation'] as const;

/** The server's `aiConversationMessages`: how many of a conversation's newest messages are read. */
export const MOCK_AI_CONVERSATION_MESSAGES = 10;

/** The server's `EarlierCutNote`. */
export const MOCK_EARLIER_CUT_NOTE = '[… earlier messages were cut off because the conversation is too long]';

/** A conversation as the mock server reads it for Summarize: members oldest first, Sent merged in. */
export interface MockReadableConversation {
  subject: string;
  /** Members the detail itself left out (older than its cap). */
  omitted: number;
  messages: { from: string; date: string; text: string }[];
}

const URL_RE = /https?:\/\/[^\s<>"'()[\]{}`]+/g;

/** The web addresses in a text, as the server's `Links` finds them. */
export function mockLinks(text: string): string[] {
  const out: string[] = [];
  for (const match of text.matchAll(URL_RE)) {
    const url = match[0].replace(/[.,;:!?*]+$/, '');
    if (url.length > 'https://'.length && !out.includes(url)) out.push(url);
  }
  return out;
}

/** The server's `SplitQuoted`, in its simplest form: from the first quote marker on. */
export function mockSplitQuoted(text: string): { own: string; quoted: string } {
  const lines = text.split('\n');
  let cut = lines.findIndex((line) => line.trimStart().startsWith('>'));
  if (cut > 0 && /(wrote|schrieb|a écrit)\s*:\s*$/i.test(lines[cut - 1] ?? '')) cut -= 1;
  if (cut <= 0) return { own: text.trim(), quoted: '' };
  const own = lines.slice(0, cut).join('\n').trim();
  return own === '' ? { own: text.trim(), quoted: '' } : { own, quoted: lines.slice(cut).join('\n').trim() };
}

function sentences(text: string): string[] {
  return text
    .replace(/\s+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter((s) => s.length > 3);
}

/** MOCK: what "Claude" says about a received message. Deterministic. */
export function mockReadingAnswer(
  action: 'summarize' | 'explain' | 'ask',
  message: MockReadableMessage,
  body: string,
  links: string[],
  question?: string,
): string {
  const said = sentences(body);
  const lead = said[0] ?? 'The message has no text.';
  const warning = message.inJunk
    ? '## Warning signs\n- It was filed as junk, so be careful with its links and attachments.\n\n'
    : '';
  if (action === 'ask') {
    return `You asked: “${question ?? ''}”. The email says: ${lead}`;
  }
  if (action === 'explain') {
    return `${warning}In plain terms, ${message.from} is writing about “${message.subject}”. ${lead}\n\n## What it means for you\n- ${said.find((s) => s.includes('?')) ?? 'Nothing needs doing.'}`;
  }
  const asks = said.filter((s) => /\?|please|could you|can you|by \w+day/i.test(s)).slice(0, 3);
  const mentioned = [
    ...links.slice(0, 3).map((url) => `- ${url}`),
    ...message.attachments.map((a) => `- ${a.name} (${a.type})`),
  ];
  return [
    `${warning}${message.from} writes about “${message.subject}”. ${lead}`,
    `## Key points\n${said.slice(0, 3).map((s) => `- ${s}`).join('\n') || '- The message has no text.'}`,
    `## Action items\n${asks.map((s) => `- ${s.replace(/\b(\w+day)\b/, '**$1**')}`).join('\n') || '- Nothing is asked of you.'}`,
    ...(mentioned.length > 0 ? [`## Links & attachments mentioned\n${mentioned.join('\n')}`] : []),
  ].join('\n\n');
}

export interface MockAiOptions {
  /** The mock's per-call latency (0 in tests). */
  delay: (signal?: AbortSignal) => Promise<void>;
  /** Pause between streamed pieces; 0 streams synchronously. */
  chunkDelayMs: number;
  /** Reads a message from the mock mailbox for Summarize; throws as `messages.get` does. */
  readMessage?: (accountId: string, messageId: string) => MockReadableMessage;
  /** Reads the conversation a message belongs to, for "Summarize conversation" (ADR 0077). */
  readConversation?: (accountId: string, messageId: string) => MockReadableConversation;
}

function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('The operation was aborted.', 'AbortError'));
      return;
    }
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
      clearTimeout(timer);
      reject(new DOMException('The operation was aborted.', 'AbortError'));
    }, { once: true });
  });
}

/**
 * MOCK: the server's `conversationForAssistant` + `Fit`: the newest
 * MOCK_AI_CONVERSATION_MESSAGES members' own text, oldest first, each under
 * its sender and date, cut from the OLDEST end, with the cut announced.
 */
export function mockConversationBody(conversation: MockReadableConversation): { body: string; truncated: boolean } {
  let members = conversation.messages;
  let truncated = conversation.omitted > 0;
  if (members.length > MOCK_AI_CONVERSATION_MESSAGES) {
    members = members.slice(-MOCK_AI_CONVERSATION_MESSAGES);
    truncated = true;
  }
  let body = members
    .map((m) => `From: ${m.from}\nDate: ${new Date(m.date).toUTCString()}\n\n${mockSplitQuoted(m.text).own}`)
    .join('\n\n---\n\n');
  const room = MOCK_MAX_EMAIL_TEXT_CHARS - [...MOCK_EARLIER_CUT_NOTE].length - 1;
  const chars = [...body];
  if (chars.length > room) {
    body = chars.slice(chars.length - room).join('');
    truncated = true;
  }
  return { body: truncated ? `${MOCK_EARLIER_CUT_NOTE}\n${body}` : body, truncated };
}

export function createMockAiApi({ delay, chunkDelayMs, readMessage, readConversation }: MockAiOptions): AiApi {
  let enabled = false;
  let model = MOCK_AI_DEFAULT_MODEL;
  let key: string | undefined;
  /** When the key's list was last fetched "from Anthropic"; reset with the key. */
  let fetchedAt: string | undefined;
  let refreshes: number[] = [];

  /** The list a key sees: its catalogue, or the built-in one when Anthropic is "down". */
  const listFor = (k: string): { models: AiListedModel[]; down: boolean } =>
    k.includes('modelsdown') ? { models: fallbackModels(), down: true } : { models: catalogueFor(k), down: false };

  /** A choice the server accepts for the saved key (the unchanged saved one always is). */
  const acceptable = (choice: string): boolean => {
    if (choice === model) return true;
    if (choice === 'latest:sonnet' || choice === 'latest:opus' || choice === 'latest:haiku') return true;
    if (MOCK_AI_MODELS.some((m) => m.id === choice)) return true;
    if (key === undefined || keyProblem(key)?.status === 422) return false;
    const list = listFor(key).models;
    if (choice === 'latest:fable') return latestOf('fable', list) !== undefined;
    return list.some((m) => m.id === choice);
  };

  /** The model a request uses now, or the error the server answers when none resolves. */
  const resolveForRequest = (k: string): AiSelectedModel & { id: string } => {
    const selected = mockResolveModel(model, listFor(k).models);
    if (selected.id === undefined) {
      throw aiError(422, ApiErrorCodes.AIModelUnavailable,
        'The model chosen in Settings → AI assistant is not available to your key. Choose another there.',
        { settings_path: '/settings/ai' });
    }
    return { ...selected, id: selected.id };
  };

  const answered = (selected: AiSelectedModel & { id: string }): AiAssistResult => ({
    stopReason: 'end_turn',
    model: selected.id,
    ...(selected.displayName !== undefined ? { modelName: selected.displayName } : {}),
    ...(selected.notice !== undefined ? { modelNotice: selected.notice } : {}),
  });
  let keySavedAt: string | undefined;
  let lastUsedAt: string | undefined;

  const stream = async (answer: string, onText: (text: string) => void, signal?: AbortSignal): Promise<void> => {
    for (let at = 0; at < answer.length; at += 6) {
      if (signal?.aborted) throw new DOMException('The operation was aborted.', 'AbortError');
      if (chunkDelayMs > 0) await wait(chunkDelayMs, signal);
      onText(answer.slice(at, at + 6));
    }
  };

  const snapshot = (): AiSettings => ({
    enabled: enabled && key !== undefined,
    model,
    defaultModel: MOCK_AI_DEFAULT_MODEL,
    models: MOCK_AI_MODELS.map((m) => ({ ...m })),
    keySaved: key !== undefined,
    ...(key !== undefined ? { keyHint: key.slice(-4) } : {}),
    ...(key !== undefined && keySavedAt !== undefined ? { keySavedAt } : {}),
    ...(lastUsedAt !== undefined ? { lastUsedAt } : {}),
    maxInputChars: MAX_INPUT_CHARS,
  });

  return {
    async settings(signal) {
      await delay(signal);
      return snapshot();
    },
    async updateSettings(input) {
      await delay();
      if (!acceptable(input.model)) {
        throw aiError(400, ApiErrorCodes.BadRequest, 'Choose one of the listed models.', { field: 'model' });
      }
      if (input.enabled && key === undefined) {
        throw aiError(400, ApiErrorCodes.BadRequest, 'Save your Anthropic API key before turning the assistant on.', { field: 'enabled' });
      }
      enabled = input.enabled;
      model = input.model;
      return snapshot();
    },
    async saveKey(next) {
      await delay();
      const trimmed = next.trim();
      if (!looksLikeKey(trimmed)) {
        throw aiError(400, ApiErrorCodes.BadRequest, 'That does not look like an API key: that is too short to be an API key.', { field: 'key' });
      }
      key = trimmed;
      keySavedAt = new Date().toISOString();
      fetchedAt = undefined;
      return snapshot();
    },
    async removeKey() {
      await delay();
      key = undefined;
      keySavedAt = undefined;
      fetchedAt = undefined;
      enabled = false;
      return snapshot();
    },
    async testKey(typed) {
      await delay();
      const candidate = typed === undefined ? key : typed.trim();
      if (candidate === undefined) {
        throw aiError(409, ApiErrorCodes.AINotEnabled, 'There is no saved API key to test.');
      }
      if (!looksLikeKey(candidate)) {
        throw aiError(400, ApiErrorCodes.BadRequest, 'That does not look like an API key.', { field: 'key' });
      }
      const problem = keyProblem(candidate);
      if (problem) throw problem;
      const selected = mockResolveModel(model, listFor(candidate).models);
      return {
        ok: true,
        model: selected.id ?? model,
        ...(selected.displayName !== undefined ? { modelName: selected.displayName } : {}),
      };
    },
    async models(options, signal) {
      await delay(signal);
      if (key === undefined) {
        throw aiError(409, ApiErrorCodes.AINotEnabled,
          'There is no Anthropic API key saved. Add one in Settings → AI assistant.',
          { settings_path: '/settings/ai' });
      }
      if (options?.refresh === true) {
        const now = Date.now();
        refreshes = refreshes.filter((at) => now - at < 60_000);
        const oldest = refreshes[0];
        if (oldest !== undefined && refreshes.length >= MOCK_AI_MODELS_REFRESH_PER_MINUTE) {
          const seconds = Math.max(1, Math.ceil((60_000 - (now - oldest)) / 1000));
          throw new ApiError(429, {
            error: {
              code: ApiErrorCodes.RateLimited,
              message: 'The model list was refreshed a moment ago. Try again in a minute.',
              request_id: 'mock-ai',
            },
          }, String(seconds));
        }
        refreshes.push(now);
      }
      // Listing models costs nothing, so only a refused key fails here.
      const refused = keyProblem(key);
      if (refused?.status === 422) throw refused;
      const { models: list, down } = listFor(key);
      const base = { models: list, latest: latestEntries(list), selected: mockResolveModel(model, list) };
      if (down) return { ...base, source: 'fallback', upstreamError: 'overloaded' } satisfies AiModels;
      const fresh = fetchedAt === undefined || options?.refresh === true;
      const at = fresh ? new Date().toISOString() : (fetchedAt ?? new Date().toISOString());
      fetchedAt = at;
      return { ...base, source: fresh ? 'live' : 'cache', fetchedAt: at } satisfies AiModels;
    },
    async assist(req, onText, signal) {
      await delay(signal);
      if (!enabled || key === undefined) {
        throw aiError(409, ApiErrorCodes.AINotEnabled,
          key === undefined
            ? 'There is no Anthropic API key saved. Add one in Settings → AI assistant.'
            : 'The AI assistant is turned off. Turn it on in Settings → AI assistant.',
          { settings_path: '/settings/ai' });
      }
      const problem = validate(req);
      if (problem !== undefined) throw aiError(400, ApiErrorCodes.BadRequest, problem);
      const size = [req.instruction, req.text, req.subject, req.thread, ...(req.attachmentNames ?? [])]
        .reduce((n, s) => n + [...(s ?? '')].length, 0);
      if (size > MAX_INPUT_CHARS) {
        throw aiError(413, ApiErrorCodes.AIInputTooLarge,
          'That is more text than the assistant takes at once. Select a shorter part and try again.',
          { max_chars: MAX_INPUT_CHARS });
      }
      const refused = keyProblem(key);
      if (refused) throw refused;
      const using = resolveForRequest(key);
      lastUsedAt = new Date().toISOString();
      await stream(mockAiAnswer(req), onText, signal);
      return answered(using);
    },
    async assistMessage(accountId, messageId, req, onText, onMeta, signal) {
      await delay(signal);
      const fields: readonly string[] = MOCK_MESSAGE_ASSIST_FIELDS;
      const unknown = Object.keys(req).filter((k) => !fields.includes(k));
      if (unknown.length > 0) throw aiError(400, ApiErrorCodes.BadRequest, `Unknown field ${unknown[0]}.`);
      if (!['summarize', 'explain', 'ask'].includes(req.action)) {
        throw aiError(400, ApiErrorCodes.BadRequest, 'Choose summarize, explain or ask.', { field: 'action' });
      }
      if (req.action !== 'ask' && req.question !== undefined && req.question !== '') {
        throw aiError(400, ApiErrorCodes.BadRequest, 'Only ask takes a question.', { field: 'question' });
      }
      if (req.action === 'ask' && (req.question ?? '').trim() === '') {
        throw aiError(400, ApiErrorCodes.BadRequest, 'Type a question about this email.', { field: 'question' });
      }
      if (req.wholeThread === true && req.conversation === true) {
        throw aiError(400, ApiErrorCodes.BadRequest, 'Choose the whole thread or the whole conversation, not both.', {
          field: 'conversation',
        });
      }
      if (readMessage === undefined || (req.conversation === true && readConversation === undefined)) {
        throw aiError(501, ApiErrorCodes.UnsupportedByHost, 'The AI assistant is not available on this server.');
      }
      if (!enabled || key === undefined) {
        throw aiError(409, ApiErrorCodes.AINotEnabled,
          key === undefined
            ? 'There is no Anthropic API key saved. Add one in Settings → AI assistant.'
            : 'The AI assistant is turned off. Turn it on in Settings → AI assistant.',
          { settings_path: '/settings/ai' });
      }
      // Read only once the assistant is known to be on, as the server does.
      const message = readMessage(accountId, messageId);
      const refused = keyProblem(key);
      if (refused) throw refused;
      const using = resolveForRequest(key);
      lastUsedAt = new Date().toISOString();

      if (req.conversation === true && readConversation !== undefined) {
        const conversation = readConversation(accountId, messageId);
        const { body, truncated } = mockConversationBody(conversation);
        const links = mockLinks(body);
        onMeta({ truncated, hasQuoted: false, wholeThread: false, conversation: true, inJunk: message.inJunk, links });
        // The canned answer quotes sentences: not the per-message headings.
        const prose = body.replace(/^(From|Date): .*$|^---$/gm, '').replace(MOCK_EARLIER_CUT_NOTE, '');
        let answer = mockReadingAnswer(req.action, { ...message, subject: conversation.subject }, prose, links, req.question?.trim());
        if (truncated) answer += '\n\nThe oldest messages of this conversation were left out because it is very long.';
        await stream(answer, onText, signal);
        return answered(using);
      }

      const { own, quoted } = mockSplitQuoted(message.text);
      const whole = req.wholeThread === true && quoted !== '';
      let body = whole ? `${own}\n\n${quoted}` : own;
      const truncated = [...body].length > MOCK_MAX_EMAIL_TEXT_CHARS;
      if (truncated) body = [...body].slice(0, MOCK_MAX_EMAIL_TEXT_CHARS).join('');
      const links = mockLinks(body);
      onMeta({ truncated, hasQuoted: quoted !== '', wholeThread: whole, conversation: false, inJunk: message.inJunk, links });
      let answer = mockReadingAnswer(req.action, message, body, links, req.question?.trim());
      if (truncated) answer += '\n\nThe email was cut off because it is very long.';
      await stream(answer, onText, signal);
      return answered(using);
    },
  };
}
